import React, { useMemo } from 'react'
import type { FieldErrors, UseFormRegister } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Icon, Input } from '@node-space/ui-components'
import type { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { BeneficiaryDetailsCurrency, PaymentMethods } from 'types/beneficiaries'

type SwiftCodeFieldProps = {
  disabled: boolean
  errors: FieldErrors<ContactFiatInstrumentFormData>
  formValues: ContactFiatInstrumentFormData
  isValidSwiftCode?: boolean
  loading?: boolean
  register: UseFormRegister<ContactFiatInstrumentFormData>
  onBlur?: () => void
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

export const SwiftCodeField = ({
  disabled,
  errors,
  formValues,
  isValidSwiftCode,
  loading,
  register,
  onChange,
  onBlur,
}: SwiftCodeFieldProps) => {
  const { t } = useTranslation()

  const { paymentMethods, currency } = formValues || {}
  const isSwiftOnly = paymentMethods?.includes(PaymentMethods.SWIFT) && paymentMethods?.length === 1
  const isEuro = currency === BeneficiaryDetailsCurrency.EUR

  const labels = useMemo(() => {
    if (isEuro) {
      return {
        label: isSwiftOnly ? t('formFields.swiftBankCode') : t('formFields.bic'),
        placeholder: isSwiftOnly
          ? t('formFields.placeholder.swiftBankCode')
          : t('formFields.placeholder.bic'),
      }
    }

    return {
      label: t('formFields.swiftBankCode'),
      placeholder: t('formFields.placeholder.swiftBankCode'),
    }
  }, [isEuro, isSwiftOnly])

  const swiftCode = formValues?.swiftCode

  return (
    <Input
      {...register('swiftCode')}
      name="swiftCode"
      testId="swiftCode"
      label={labels.label}
      placeholder={labels.placeholder}
      errorText={errors?.swiftCode?.message}
      error={!!errors?.swiftCode?.message}
      onChange={onChange}
      onBlur={onBlur}
      loading={loading}
      IconButton={
        isValidSwiftCode && swiftCode ? (
          <Button variant="ghost" iconElement={<Icon name="Check" />} />
        ) : undefined
      }
      iconPosition="right"
      disabled={disabled}
    />
  )
}
